import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/authcontext';
import { getContacts, createContact, updateContact, deleteContact } from '../services/api';
import ContactForm from '../components/contactform/contactform';
import styles from './addressbookpage.module.css';
import tableStyles from '../components/bidtable/bidtable.module.css';
import Papa from 'papaparse';
import { db } from '../services/firebase';
import { collection, writeBatch, doc, getDocs, query, where, getDoc, doc as firestoreDoc } from 'firebase/firestore';

function AddressBookPage() {
  const { type } = useParams(); // 'subcontractors' or 'clients'
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingContactData, setEditingContactData] = useState(null);
  const [sort, setSort] = useState({ key: '', order: '' });
  const [filters, setFilters] = useState({ search: '', trade: '' });
  const fileInputRef = useRef(null);

  // Format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Move fetchContacts outside useEffect so it can be called after create/update/delete
  const fetchContacts = useCallback(async () => {
    if (!user) {
      setError('You must be logged in to view contacts.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const contactsRef = collection(db, 'contacts');
      const q = query(
        contactsRef,
        where('type', '==', type === 'subcontractors' ? 'subcontractor' : 'client'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      const fetchedContacts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setContacts(fetchedContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error.message);
      setError(`Failed to fetch contacts: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [type, user]);

  useEffect(() => {
    fetchContacts();
  }, [type, user, fetchContacts]);

  const handleCreateContact = async (contactData) => {
    setError(null);
    setErrorDetails(null);
    try {
      console.log('AddressBookPage: Creating contact with data:', contactData);
      await createContact(user.uid, contactData);
      setShowForm(false);
      fetchContacts();
    } catch (err) {
      console.error('AddressBookPage: Failed to create contact:', err);
      setError('Failed to create contact. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    }
  };

  const handleUpdateContact = async (updatedData) => {
    if (!editingContact) return;
    setError(null);
    setErrorDetails(null);
    try {
      console.log('AddressBookPage: Updating contact with ID:', editingContact, 'with data:', updatedData);
      await updateContact(editingContact, updatedData);
      setEditingContact(null);
      setEditingContactData(null);
      fetchContacts();
    } catch (err) {
      console.error('AddressBookPage: Failed to update contact:', err);
      setError('Failed to update contact. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    }
  };

  const handleEdit = (contact) => {
    console.log('AddressBookPage: Edit contact:', contact);
    setEditingContact(contact.id);
    setEditingContactData(contact);
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      setError(null);
      setErrorDetails(null);
      try {
        console.log('AddressBookPage: Deleting contact with ID:', contactId);
        await deleteContact(contactId);
        fetchContacts();
      } catch (err) {
        console.error('AddressBookPage: Failed to delete contact:', err);
        setError('Failed to delete contact. Please try again later.');
        setErrorDetails(err.message || 'Unknown error occurred');
      }
    }
  };

  const handleSort = (key) => {
    const order = sort.key === key && sort.order === 'asc' ? 'desc' : 'asc';
    setSort({ key, order });
  };

  const getSortArrow = (col) => {
    if (sort.key === col) {
      return sort.order === 'asc' ? ' ▼' : ' ▲';
    }
    return '';
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Define columns based on contact type
  const columns = type === 'subcontractors' 
    ? [
        { key: 'trade', label: 'Trade' },
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'address', label: 'Address' },
      ]
    : [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'address', label: 'Address' },
      ];

  // Get unique trades for filter dropdown
  const uniqueTrades = useMemo(() => {
    if (type !== 'subcontractors') return [];
    
    const trades = contacts
      .map(contact => contact.trade)
      .filter(Boolean) // Remove undefined/null values
      .sort();
    
    // Remove duplicates
    return [...new Set(trades)];
  }, [contacts, type]);

  // Apply filters to contacts
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const contactValues = Object.values(contact).filter(val => typeof val === 'string');
        const matchesSearch = contactValues.some(val => 
          val.toLowerCase().includes(searchTerm)
        );
        
        if (!matchesSearch) return false;
      }
      
      // Trade filter (for subcontractors only)
      if (type === 'subcontractors' && filters.trade && contact.trade !== filters.trade) {
        return false;
      }
      
      return true;
    });
  }, [contacts, filters, type]);

  // Sort the contacts based on the sort state
  const sortedContacts = useMemo(() => {
    return [...filteredContacts].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key];
      let bValue = b[sort.key];
      if (sort.key === 'createdAt') {
        return sort.order === 'asc' 
          ? new Date(aValue) - new Date(bValue) 
          : new Date(bValue) - new Date(aValue);
      }
      const aString = String(aValue || '').toLowerCase();
      const bString = String(bValue || '').toLowerCase();
      if (sort.order === 'asc') {
        return aString.localeCompare(bString);
      } else {
        return bString.localeCompare(aString);
      }
    });
  }, [filteredContacts, sort, type]);

  // Display title based on type
  const pageTitle = type === 'subcontractors' ? 'Subcontractor Address Book' : 'Client Address Book';
  const addButtonText = `Add New ${type === 'subcontractors' ? 'Subcontractor' : 'Client'}`;
  const searchPlaceholder = `Search ${type === 'subcontractors' ? 'subcontractors' : 'clients'}...`;

  // CSV Import Handler
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // Reset before click to allow re-importing same file
      fileInputRef.current.click();
    } else {
      console.error('File input ref is not set');
    }
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.warn('No file selected');
      return;
    }
    if (!user) {
      setError('You must be logged in to import contacts.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: async (result) => {
        try {
          // Fetch companyId for the current user
          const userDoc = await getDoc(firestoreDoc(db, 'users', user.uid));
          const companyId = userDoc.exists() ? userDoc.data().companyId : null;
          if (!companyId) {
            throw new Error('Could not determine your company. Please contact support.');
          }
          // Validate CSV columns based on section
          const expectedColumns = type === 'subcontractors' 
            ? ['trade', 'name', 'email', 'phone', 'address']
            : ['name', 'email', 'phone', 'address'];
          const actualColumns = Object.keys(result.data[0] || {}).map(col => col.trim().toLowerCase());
          const missingColumns = expectedColumns.filter(col => !actualColumns.includes(col));
          if (missingColumns.length > 0) {
            throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
          }

          const batch = writeBatch(db);
          const newContacts = [];
          result.data.forEach((row) => {
            const docRef = doc(collection(db, 'contacts'));
            const normalizedRow = {};
            Object.keys(row).forEach(key => {
              normalizedRow[key.trim().toLowerCase()] = row[key];
            });
            const contactData = {
              userId: user.uid,
              companyId,
              type: type === 'subcontractors' ? 'subcontractor' : 'client',
              ...(type === 'subcontractors' ? { trade: normalizedRow.trade || '' } : {}),
              ...(type === 'subcontractors' ? { subcontractorName: normalizedRow.name || '' } : {}),
              name: normalizedRow.name || '',
              email: normalizedRow.email || '',
              phone: normalizedRow.phone || '',
              address: normalizedRow.address || ''
            };
            batch.set(docRef, contactData);
            newContacts.push({ id: docRef.id, ...contactData });
          });
          await batch.commit();
          const contactsRef = collection(db, 'contacts');
          const q = query(
            contactsRef,
            where('type', '==', type === 'subcontractors' ? 'subcontractor' : 'client'),
            where('userId', '==', user.uid)
          );
          const snapshot = await getDocs(q);
          const fetchedContacts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setContacts(fetchedContacts);
          alert('Successfully imported contacts!');
        } catch (error) {
          console.error('Error importing contacts:', error.message);
          setError(`Failed to import contacts: ${error.message}`);
        } finally {
          setLoading(false);
          event.target.value = null;
        }
      },
      error: (error) => {
        console.error('Error parsing CSV:', error.message);
        setError(`Failed to parse CSV: ${error.message}`);
        setLoading(false);
        event.target.value = null;
      }
    });
  };

  if (loading) {
    return <div className={styles.loading}>Loading contacts...</div>;
  }

  if (error) {
    return (
      <div className={styles.error}>
        <div>{error}</div>
        {errorDetails && (
          <div className={styles.errorDetails}>
            <p>Error details: {errorDetails}</p>
            {errorDetails.includes('permission') && (
              <p>
                This may be due to insufficient Firestore permissions. Please make sure 
                you have updated the security rules in the Firebase Console to allow
                access to the contacts collection.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.pageTitle}>{pageTitle}</h2>
      
      <div className={styles.actionsContainer}>
        <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2a2a42',
              color: '#e0e0e0',
              border: '1px solid #4a5568',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            Add {type === 'subcontractors' ? 'Sub' : 'Client'}
          </button>
          <button
            onClick={handleImportClick}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4A90E2',
              color: '#e0e0e0',
              border: '1px solid #4a5568',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            Import Template
          </button>
          <a
            href={type === 'subcontractors' ? '/SubAddressBook_Template.csv' : '/ClientAddressBook_Template.csv'}
            download={type === 'subcontractors' ? 'SubAddressBook_Template.csv' : 'ClientAddressBook_Template.csv'}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4A90E2',
              color: '#e0e0e0',
              border: '1px solid #4a5568',
              borderRadius: '4px',
              textDecoration: 'none',
              fontSize: '14px',
              display: 'inline-block',
              textAlign: 'center',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            Download Template
          </a>
          <input
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
        </div>
      </div>
      
      {showForm ? (
        <ContactForm 
          onSubmit={handleCreateContact} 
          onCancel={() => setShowForm(false)} 
          type={type}
        />
      ) : editingContact ? (
        <ContactForm 
          onSubmit={handleUpdateContact} 
          onCancel={() => {
            setEditingContact(null);
            setEditingContactData(null);
          }} 
          defaultValues={editingContactData}
          type={type}
        />
      ) : (
        <>
          <div className={styles.filtersContainer}>
            <div className={styles.filterGroup}>
              <input 
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder={searchPlaceholder}
                className={styles.searchInput}
              />
            </div>
            
            {type === 'subcontractors' && (
              <div className={styles.filterGroup}>
                <select
                  name="trade"
                  value={filters.trade}
                  onChange={handleFilterChange}
                  className={styles.tradeFilter}
                  aria-label="Filter by Trade"
                >
                  <option value="">All Trades</option>
                  {uniqueTrades.map(trade => (
                    <option key={trade} value={trade}>{trade}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {filteredContacts.length === 0 ? (
            <div className={styles.empty}>
              {filters.search || (type === 'subcontractors' && filters.trade) ? 
                `No ${type} found matching your search criteria.` : 
                `No ${type} found. Click "${addButtonText}" to create one.`}
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={tableStyles.table}>
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th key={col.key} onClick={() => handleSort(col.key)}>
                        {col.label}
                        {sort.key === col.key && (
                          <span className={tableStyles.sortArrow}>{getSortArrow(col.key)}</span>
                        )}
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContacts.map(contact => (
                    <tr key={contact.id}>
                      {columns.map(col => (
                        <td key={col.key}>
                          {col.key === 'createdAt' ? formatDate(contact[col.key]) : contact[col.key]}
                        </td>
                      ))}
                      <td className={tableStyles.actions}>
                        <button 
                          onClick={() => handleEdit(contact)} 
                          className={tableStyles.editButton}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(contact.id)} 
                          className={tableStyles.deleteButton}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AddressBookPage; 